import React, { useState, useEffect, useCallback } from 'react';
import { getSupabaseBrowser } from '../supabaseClient';
import { getSessionId } from '../lib/session';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function logEvent(homeCity, destinationCity, eventType) {
  const supabase = getSupabaseBrowser();
  if (!supabase) return;
  supabase
    .from('gate_events')
    .insert({
      session_id: getSessionId(),
      home_city: String(homeCity ?? '').toLowerCase().trim(),
      destination_city: String(destinationCity ?? '').toLowerCase().trim(),
      event_type: eventType,
    })
    .then(({ error }) => {
      if (error) console.error('gate_events insert:', error.message);
    });
}

export default function EmailGate({ isOpen, homeCity, destinationCity, onProceed, onClose }) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  // Log gate_shown when modal opens
  useEffect(() => {
    if (!isOpen) return;
    logEvent(homeCity, destinationCity, 'gate_shown');
    setEmail('');
    setEmailError(false);
  }, [isOpen, homeCity, destinationCity]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);

    const supabase = getSupabaseBrowser();
    if (supabase) {
      try {
        await supabase.from('email_captures').insert({
          email: trimmed.toLowerCase(),
          home_city: String(homeCity ?? '').toLowerCase().trim(),
          destination_city: String(destinationCity ?? '').toLowerCase().trim(),
          source: 'itinerary_gate',
        });
      } catch (err) {
        console.error('email_captures upsert:', err);
        // Never block the user — fall through
      }
      logEvent(homeCity, destinationCity, 'email_submitted');
    }

    onProceed(trimmed.toLowerCase());
  }, [email, homeCity, destinationCity, onProceed]);

  const handleSkip = useCallback(() => {
    logEvent(homeCity, destinationCity, 'skipped');
    onProceed();
  }, [homeCity, destinationCity, onProceed]);

  const handleClose = useCallback(() => {
    logEvent(homeCity, destinationCity, 'closed');
    onClose();
  }, [homeCity, destinationCity, onClose]);

  if (!isOpen) return null;

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');
        .eg-input:focus { border-bottom-color: #c8a96e !important; outline: none; }
        .eg-submit:hover { opacity: 0.88; }
        .eg-skip:hover { text-decoration: underline; }
      `}</style>

      {/* Card — stop propagation so clicks inside don't close */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: '#2a2825',
          maxWidth: 480,
          width: '100%',
          padding: '48px',
          borderRadius: 4,
        }}
      >
        {/* Close X */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 18,
            right: 22,
            fontSize: 20,
            color: '#524e49',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>

        {/* Eyebrow */}
        <div style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#8a8278',
          marginBottom: 14,
        }}>
          Your Itinerary
        </div>

        {/* Headline */}
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 28,
          fontWeight: 700,
          color: '#f0ebe3',
          lineHeight: 1.25,
          margin: '0 0 12px',
        }}>
          Your day in {destinationCity} is{' '}
          <em style={{ fontStyle: 'italic', color: '#c8a96e' }}>almost ready.</em>
        </h2>

        {/* Subhead */}
        <p style={{
          fontSize: 16,
          color: '#8a8278',
          margin: '0 0 28px',
          lineHeight: 1.5,
        }}>
          Where should we send it?
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <input
            className="eg-input"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(false); }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder="you@example.com"
            autoComplete="email"
            style={{
              display: 'block',
              width: '100%',
              background: 'transparent',
              border: 'none',
              borderBottom: `1px solid ${inputFocused ? '#c8a96e' : '#524e49'}`,
              padding: '12px 0',
              fontSize: 15,
              color: '#f0ebe3',
              fontFamily: "'DM Sans', sans-serif",
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
          />
          {emailError && (
            <div style={{ fontSize: 12, color: '#c8a96e', marginTop: 6 }}>
              Please enter a valid email.
            </div>
          )}

          <p style={{
            fontSize: 11,
            color: '#524e49',
            margin: '10px 0 24px',
            lineHeight: 1.5,
          }}>
            We'll send your itinerary and occasional updates. Nothing else.
          </p>

          <button
            className="eg-submit"
            type="submit"
            style={{
              display: 'block',
              width: '100%',
              padding: '14px',
              background: '#c8a96e',
              color: '#000',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
              transition: 'opacity 0.15s',
              marginBottom: 16,
            }}
          >
            Generate my day →
          </button>
        </form>

        {/* Skip */}
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            className="eg-skip"
            onClick={handleSkip}
            style={{
              fontSize: 13,
              color: '#8a8278',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'none',
              fontFamily: "'DM Sans', sans-serif",
              padding: 0,
            }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
